import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';

// CRM
import Clients from './pages/crm/Clients';
import ClientDetail from "./pages/crm/ClientDetail";
import Contacts from './pages/crm/Contacts';
import Leads from './pages/crm/Leads';
import Deals from './pages/crm/Deals';

// Sales
import Quotations from './pages/sales/Quotations';
import MayaLeadsInbox from "./pages/sales/MayaLeadsInbox";
import SalesOrders from './pages/sales/SalesOrders';
import Estimates from './pages/sales/Estimates';
import PaymentReceipts from './pages/sales/PaymentReceipts';
import DeliveryChallan from './pages/sales/DeliveryChallan';
import CreditNotes from './pages/sales/CreditNotes';

// Projects
import Projects from './pages/projects/Projects';
import Sites from './pages/projects/Sites';
import Tasks from './pages/projects/Tasks';
import Tickets from './pages/projects/Tickets';

// Procurement
import Suppliers from './pages/procurement/Suppliers';
import Products from './pages/procurement/Products';
import PurchaseOrders from './pages/procurement/PurchaseOrders';
import Bills from './pages/procurement/Bills';
import Payments from './pages/procurement/Payments';

// Inventory
import Warehouses from './pages/inventory/Warehouses';
import Stock from './pages/inventory/Stock';

// Finance
import Invoices from './pages/finance/Invoices';
import Expenses from './pages/finance/Expenses';
import ChartOfAccounts from './pages/finance/ChartOfAccounts';
import JournalEntries from './pages/finance/JournalEntries';

// HR
import Employees from './pages/hr/Employees';
import Departments from './pages/hr/Departments';
import Attendance from './pages/hr/Attendance';
import Payroll from './pages/hr/Payroll';
import Leave from './pages/hr/Leave';

// Other
import POS from './pages/POS';
import Documents from './pages/Documents';
import Currencies from './pages/Currencies';
import Settings from './pages/Settings';
import Reports from './pages/Reports';

// Admin
import Companies from './pages/admin/Companies';
import Users from './pages/admin/Users';
import Roles from './pages/admin/Roles';
import MasterDashboard from './pages/admin/MasterDashboard';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

      {/* CRM */}
      <Route path="/crm/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
  <Route path="/crm/clients/:id" element={<ProtectedRoute><ClientDetail /></ProtectedRoute>} />
      <Route path="/crm/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
      <Route path="/crm/leads" element={<ProtectedRoute><Leads /></ProtectedRoute>} />
      <Route path="/crm/deals" element={<ProtectedRoute><Deals /></ProtectedRoute>} />

      {/* Sales */}
      <Route path="/sales/quotations" element={<ProtectedRoute><Quotations /></ProtectedRoute>} />
<Route path="/sales/maya-leads" element={<ProtectedRoute><MayaLeadsInbox /></ProtectedRoute>} />
      <Route path="/sales/orders" element={<ProtectedRoute><SalesOrders /></ProtectedRoute>} />
          {/* Round 10: legacy alias for old links; canonical detail route is /crm/clients/:id */}
  <Route path="/sales/clients/:id" element={<ProtectedRoute><ClientDetail /></ProtectedRoute>} />
      <Route path="/sales/estimates" element={<ProtectedRoute><Estimates /></ProtectedRoute>} />
      <Route path="/sales/delivery-challan" element={<ProtectedRoute><DeliveryChallan /></ProtectedRoute>} />
      <Route path="/sales/receipts" element={<ProtectedRoute><PaymentReceipts /></ProtectedRoute>} />
      <Route path="/sales/credit-notes" element={<ProtectedRoute><CreditNotes /></ProtectedRoute>} />

      {/* Projects */}
      <Route path="/projects/list" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
      <Route path="/projects/sites" element={<ProtectedRoute><Sites /></ProtectedRoute>} />
      <Route path="/projects/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
      <Route path="/projects/tickets" element={<ProtectedRoute><Tickets /></ProtectedRoute>} />

      {/* Procurement */}
      <Route path="/procurement/suppliers" element={<ProtectedRoute><Suppliers /></ProtectedRoute>} />
      <Route path="/procurement/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
      <Route path="/procurement/orders" element={<ProtectedRoute><PurchaseOrders /></ProtectedRoute>} />
      <Route path="/procurement/bills" element={<ProtectedRoute><Bills /></ProtectedRoute>} />
      <Route path="/procurement/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />

      {/* Inventory */}
      <Route path="/inventory/warehouses" element={<ProtectedRoute><Warehouses /></ProtectedRoute>} />
      <Route path="/inventory/stock" element={<ProtectedRoute><Stock /></ProtectedRoute>} />

      {/* Finance */}
      <Route path="/finance/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
      <Route path="/finance/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
      <Route path="/finance/accounts" element={<ProtectedRoute><ChartOfAccounts /></ProtectedRoute>} />
      <Route path="/finance/journal" element={<ProtectedRoute><JournalEntries /></ProtectedRoute>} />

      {/* HR */}
      <Route path="/hr/employees" element={<ProtectedRoute><Employees /></ProtectedRoute>} />
      <Route path="/hr/departments" element={<ProtectedRoute><Departments /></ProtectedRoute>} />
      <Route path="/hr/attendance" element={<ProtectedRoute><Attendance /></ProtectedRoute>} />
      <Route path="/hr/payroll" element={<ProtectedRoute><Payroll /></ProtectedRoute>} />
      <Route path="/hr/leave" element={<ProtectedRoute><Leave /></ProtectedRoute>} />

      {/* Other */}
      <Route path="/pos" element={<ProtectedRoute><POS /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
      <Route path="/currencies" element={<ProtectedRoute><Currencies /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

      {/* Admin */}
      <Route path="/admin/master-dashboard" element={<ProtectedRoute><MasterDashboard /></ProtectedRoute>} />
      <Route path="/admin/companies" element={<ProtectedRoute><Companies /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
      <Route path="/admin/roles" element={<ProtectedRoute><Roles /></ProtectedRoute>} />

      {/* Reports */}
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />

      {/* Default Redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              fontFamily: 'Inter, sans-serif',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
